import {Text} from '@phonectas/unifon-ui-kit';

const COUNTRY_CODE = '+47';
const FULL_NUMBER_LENGTH = 11;
const COUNTRY_CODE_ALT = '0047';

/**
 * @description Convert bytes to abbreviated string (1024 -> 1 KB)
 * @param {number} bytes
 * @param {number=} decimals
 * @return {string}
 */
export function abbreviateBytes(bytes: number, decimals = 0) {
	if (bytes === 0) return '0 b';
	const k = 1024;
	const dm = decimals < 0 ? 0 : decimals;
	const sizes = ['b', 'Kb', 'Mb', 'Gb', 'Tb', 'Pb', 'Eb', 'Zb', 'Yb'];
	const i = Math.floor(Math.log(bytes) / Math.log(k));
	return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
}

/**
 *
 * @param	{number|string}		value
 * @param	{string} 			separator
 * @return	{string}
 */
export function formatThousandsWithSeparator(value: string | number, separator = ' ') {
	return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, separator);
}

/**
 *
 * @param	{number} 	value
 * @param	{number} 	decimal
 * @param	{string} 	separator
 * @return	{string}
 */
export function addDecimalsWithSeparator(value: number, decimal = 1, separator = ',') {
	return value.toFixed(decimal).replace('.', separator);
}

/**
 * @param {number} amount
 * @param {boolean} showCurrency
 * @return {string}
 */
export function toLocalCurrency(amount: number, showCurrency = false) {
	// Convert øre to kroner
	const kroner = amount / 100;
	// Format the amount in NOK
	const formattedAmount = new Intl.NumberFormat('nb-NO', {
		style: showCurrency ? 'currency' : 'decimal',
		currency: 'NOK',
		minimumFractionDigits: 2,
		maximumFractionDigits: 2
	}).format(kroner);

	return formattedAmount;
}

/**
 * @param {String} number
 * @return {String}
 */
export function displayMobileNumber(number: string) {
	if (!number) {
		return '';
	}
	// +4721619302 -> 216 19 302
	return number.replace(/\D*(\d{2})\D*(\d{3})\D*(\d{2})\D*(\d{3})\D*/, '$2 $3 $4');
}

/**
 * @param {String} number
 * @return {String}
 */
function displayFixedNumber(number: string | number) {
	if (typeof number === 'number') {
		number = number.toString();
	}

	if (!number) {
		return '';
	}
	// +4721619302 -> 21 61 93 02
	return number.replace(/\D*(\d{2})\D*(\d{2})\D*(\d{2})\D*(\d{2})\D*(\d{2})\D*/, '$2 $3 $4 $5');
}

/**
 * @param  {String}   phone
 * @return {String}
 */
export function phoneNumber(phone: string) {
	if (!phone) {
		return '';
	}
	if (phone.startsWith(COUNTRY_CODE)) {
		const withoutCountryCode = phone.replace(/\+47(.*)/, '$1');
		if (withoutCountryCode.length === FULL_NUMBER_LENGTH - COUNTRY_CODE.length) {
			if (/^([489])/.test(withoutCountryCode)) {
				return displayMobileNumber(phone);
			}
			if (/^([23567])/.test(withoutCountryCode)) {
				return displayFixedNumber(phone);
			}
		}
		return withoutCountryCode;
	}
	return phone;
}

export function phoneNumberWithoutSpaces(phone: string) {
	if (!phone) {
		return '';
	}
	if (phone.startsWith(COUNTRY_CODE) || phone.startsWith(COUNTRY_CODE_ALT)) {
		const withoutCountryCode = phone
			// .replace(/\+47(.*)/, '$1');
			.replace(/^\+47/, '')
			.replace(/^0047/, '');
		if (withoutCountryCode.length === FULL_NUMBER_LENGTH - COUNTRY_CODE.length) {
			if (/^([489])/.test(withoutCountryCode)) {
				return withoutCountryCode; // Return mobile number without spaces
			}
			if (/^([23567])/.test(withoutCountryCode)) {
				return withoutCountryCode; // Return fixed number without spaces
			}
		}
		return withoutCountryCode;
	}
	return phone;
}

export function formatNumber(numString: string | number) {
	// Convert the string to a number and check if it's over 5 digits
	if (!numString) {
		return '0,-';
	}
	if (Number(numString) >= 10000) {
		// If it's over 5 digits, format it with a comma
		return Number(numString).toLocaleString() + ',-';
	} else {
		const displayNumber = `${numString},-`;
		return displayNumber;
	}
}

export function displayInvoiceNumber(number: number | string) {
	if (typeof number === 'string') {
		number = parseInt(number, 10);
	}
	if (number >= 0) {
		return <Text>{number}</Text>;
	} else {
		return <Text color="error">{number}</Text>;
	}
}

/**
 * Converts an amount in NOK øre to kroner and formats it as a string.
 * @param {number|string} ore - The amount in øre (1 NOK = 100 øre), as an integer or a string.
 * * @param {boolean} [toFixed=false] - If true, formats the result to two decimal places.
 * @return {string} - The equivalent amount in kroner formatted as 'xxx,yy,-', or undefined for invalid input.
 */
export function convertToKroner(
	ore: number | string,
	toFixed?: boolean,
	noFormatting?: boolean
): string | undefined | null {
	// Convert string input to number if necessary
	if (ore === undefined || ore === null) return;
	if (typeof ore === 'string') {
		ore = parseInt(ore, 10);
	}

	// Validate that the input is an integer
	if (typeof ore !== 'number' || !Number.isInteger(ore)) {
		return;
	}

	const kroner = toFixed ? (ore / 100).toFixed(2) : (ore / 100).toString();
	const formattedKroner = noFormatting ? kroner : kroner.replace('.', ',');
	return `${formattedKroner},-`;
}

/**
 * Converts an amount in NOK øre (integer) to kroner as a string with two decimals.
 * Example: 246950 -> "2469.50"
 * @param {number} ore - The amount in øre (must be an integer).
 * @return {string | undefined} - The equivalent amount in kroner as a string, or undefined for invalid input.
 */
export function convertOreToKronerString(ore: number | undefined): string  {
	if (ore === undefined || ore === null) return '';
	if (typeof ore !== 'number' || !Number.isInteger(ore)) {
		return '';
	}
	return (ore / 100).toFixed(2);
}
